import React, { useContext } from "react";
import {
  Box,
  Card,
  Container,
  FormHelperText,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/auth-context";
import { useTranslation } from "react-i18next";

export default function Login() {
  const { t } = useTranslation(["auth", "validation", "common"]);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useContext(AuthContext);
  const from = location.state?.from?.pathname || "/";

  const schema = yup.object({
    email: yup
      .string()
      .required(t("requiredValidation", { field: t("email") })),
    password: yup
      .string()
      .required(t("requiredValidation", { field: t("password") })),
  });
  const {
    handleSubmit,
    formState: { errors },
    setError,
    control,
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    try {
      await login(data.email, data.password);
      navigate(from);
    } catch (error) {
      if (error.response?.data?.error === "email_not_verified") {
        navigate("/resend-email-verification", { state: { email: data.email } });
      } else {
        const errorMessage =
          error.response?.data?.error_message || t("loginError");
        setError("apiError", { message: errorMessage });
      }
    }
  };

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Card
        sx={{
          px: 5,
          py: 3,
          width: "400px",
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          margin: "auto",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "26px",
            textTransform: "uppercase",
            mt: "15px",
          }}
        >
          {t("login")}
        </Typography>
        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            marginTop: "20px",
          }}
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <Controller
            render={({ field }) => (
              <TextField
                error={!!errors.email}
                fullWidth
                size="small"
                helperText={errors.email?.message}
                label={t("email")}
                margin="normal"
                name="email"
                type="email"
                variant="filled"
                {...field}
              />
            )}
            control={control}
            name="email"
          />
          <Controller
            render={({ field }) => (
              <TextField
                fullWidth
                size="small"
                error={!!errors.password}
                helperText={errors.password?.message}
                label={t("password")}
                margin="normal"
                name="password"
                type="password"
                variant="filled"
                {...field}
              />
            )}
            control={control}
            name="password"
          />
          {errors.apiError && (
            <Box sx={{ mt: 1 }}>
              <FormHelperText error>{errors.apiError.message}</FormHelperText>
            </Box>
          )}
          <Button variant="contained" type="submit" sx={{ mt: 2 }}>
            {t("login")}
          </Button>
          <Box sx={{ mt: 3 }}>
            <Link
              component={RouterLink}
              to="/forgot-password"
              sx={{ color: "inherit" }}
            >
              {t("forgotPasswordQuestion")}
            </Link>
          </Box>
        </Box>
      </Card>
    </Container>
  );
}
